"use client";

import { CreateGameDialog } from "@/app/oyun/_components/CreateGameDialog";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/shadcn/accordion";
import { Button } from "@/components/shadcn/button";
import { Separator } from "@/components/shadcn/separator";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/shadcn/sheet";
import { Link } from "@/i18n/navigation";
import { useSession } from "@/lib/auth/client";
import { isTournamentsEnabled } from "@/lib/feature-flags";
import { Crown, GraduationCap, Menu, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { ReactNode, useState } from "react";
import {
    communityLinks,
    learnLinks,
    puzzleLinks,
    SHOW_LEARN_LINKS,
    storeLink,
    videoLinks,
} from "./nav-links";
import { useNavbarPlay } from "./use-navbar-play";
import { useSignOut } from "./use-sign-out";

function MenuLink({
    href,
    onNavigate,
    children,
}: {
    href: string;
    onNavigate: () => void;
    children: ReactNode;
}) {
    return (
        <Button variant="ghost" className="w-full justify-start" asChild>
            <Link href={href} onClick={onNavigate}>
                {children}
            </Link>
        </Button>
    );
}

export function NavbarMobileMenu() {
    const t = useTranslations("nav");
    const { data: session, isPending } = useSession();
    const isAuthenticated = Boolean(session?.user);
    const isPremium = session?.user?.isPremium === true;
    const [open, setOpen] = useState(false);
    const { isCreateGameDialogOpen, setIsCreateGameDialogOpen, startPlaying } =
        useNavbarPlay(isAuthenticated);
    const { signOut } = useSignOut();

    const closeMenu = () => setOpen(false);

    return (
        <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label={t("menu")}>
                        <Menu />
                    </Button>
                </SheetTrigger>
                <SheetContent className="flex w-80 flex-col gap-0 overflow-y-auto p-0">
                    <SheetHeader className="border-b px-4 py-4">
                        <SheetTitle>{t("menu")}</SheetTitle>
                    </SheetHeader>

                    <nav
                        aria-label={t("menu")}
                        className="flex flex-1 flex-col gap-1 p-4"
                    >
                        <Accordion type="multiple">
                            <AccordionItem value="videos" className="border-0">
                                <AccordionTrigger className="px-4 py-2 text-sm hover:no-underline">
                                    {t("videos")}
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-1 pb-1 pl-4">
                                    {videoLinks.map((link) => (
                                        <MenuLink
                                            onNavigate={closeMenu}
                                            key={link.href}
                                            href={link.href}
                                        >
                                            {t(link.labelKey)}
                                        </MenuLink>
                                    ))}
                                </AccordionContent>
                            </AccordionItem>

                            <AccordionItem value="puzzles" className="border-0">
                                <AccordionTrigger className="px-4 py-2 text-sm hover:no-underline">
                                    {t("bulmacalar")}
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-1 pb-1 pl-4">
                                    {puzzleLinks.map((link) => (
                                        <MenuLink
                                            onNavigate={closeMenu}
                                            key={link.href}
                                            href={link.href}
                                        >
                                            {t(link.labelKey)}
                                        </MenuLink>
                                    ))}
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>

                        <Button
                            type="button"
                            variant="ghost"
                            className="justify-start"
                            onClick={() => {
                                closeMenu();
                                startPlaying();
                            }}
                        >
                            {t("oyna")}
                        </Button>

                        {isTournamentsEnabled() && (
                            <>
                                <MenuLink
                                    onNavigate={closeMenu}
                                    href="/turnuva"
                                >
                                    {t("turnuvalar")}
                                </MenuLink>
                                <MenuLink onNavigate={closeMenu} href="/takim">
                                    {t("takimlar")}
                                </MenuLink>
                            </>
                        )}

                        <MenuLink onNavigate={closeMenu} href="/etkinlikler">
                            {t("etkinlikler")}
                        </MenuLink>

                        <Accordion type="multiple">
                            <AccordionItem value="other" className="border-0">
                                <AccordionTrigger className="px-4 py-2 text-sm hover:no-underline">
                                    {t("other")}
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-1 pb-1 pl-4">
                                    <MenuLink
                                        onNavigate={closeMenu}
                                        href="/egitmenler"
                                    >
                                        {t("egitmenler")}
                                    </MenuLink>
                                    {communityLinks
                                        .filter(
                                            (link) =>
                                                link.href !== "/etkinlikler",
                                        )
                                        .map((link) => (
                                            <MenuLink
                                                onNavigate={closeMenu}
                                                key={link.href}
                                                href={link.href}
                                            >
                                                {t(link.labelKey)}
                                            </MenuLink>
                                        ))}
                                    <MenuLink
                                        onNavigate={closeMenu}
                                        href={storeLink.href}
                                    >
                                        {t(storeLink.labelKey)}
                                    </MenuLink>
                                </AccordionContent>
                            </AccordionItem>

                            {SHOW_LEARN_LINKS && (
                                <AccordionItem
                                    value="learn"
                                    className="border-0"
                                >
                                    <AccordionTrigger className="px-4 py-2 text-sm hover:no-underline">
                                        {t("learn")}
                                    </AccordionTrigger>
                                    <AccordionContent className="flex flex-col gap-1 pb-1 pl-4">
                                        {learnLinks.map((link) => (
                                            <MenuLink
                                                onNavigate={closeMenu}
                                                key={link.href}
                                                href={link.href}
                                            >
                                                {t(link.labelKey)}
                                            </MenuLink>
                                        ))}
                                    </AccordionContent>
                                </AccordionItem>
                            )}
                        </Accordion>

                        <Separator className="my-2" />

                        <MenuLink
                            onNavigate={closeMenu}
                            href="/kullanici/siniflar"
                        >
                            <Users data-icon="inline-start" />
                            {t("studentTracking")}
                        </MenuLink>

                        <MenuLink onNavigate={closeMenu} href="/egitime-destek">
                            <GraduationCap data-icon="inline-start" />
                            {t("egitimeDestek")}
                        </MenuLink>

                        {!isPending && (
                            <>
                                <Separator className="my-2" />

                                <MenuLink
                                    onNavigate={closeMenu}
                                    href="/premium"
                                >
                                    <Crown data-icon="inline-start" />
                                    {isPremium ? t("premium") : t("premiumOl")}
                                </MenuLink>

                                {session?.user ? (
                                    <>
                                        <MenuLink
                                            onNavigate={closeMenu}
                                            href="/kullanici"
                                        >
                                            {t("profile")}
                                        </MenuLink>
                                        <MenuLink
                                            onNavigate={closeMenu}
                                            href="/kullanici/ayarlar"
                                        >
                                            {t("settings")}
                                        </MenuLink>
                                        <MenuLink
                                            onNavigate={closeMenu}
                                            href="/kullanici/abonelik"
                                        >
                                            {t("premiumSettings")}
                                        </MenuLink>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            className="text-destructive hover:text-destructive justify-start"
                                            onClick={() => {
                                                closeMenu();
                                                void signOut();
                                            }}
                                        >
                                            {t("logout")}
                                        </Button>
                                    </>
                                ) : (
                                    <MenuLink
                                        onNavigate={closeMenu}
                                        href="/login"
                                    >
                                        {t("login")}
                                    </MenuLink>
                                )}
                            </>
                        )}
                    </nav>
                </SheetContent>
            </Sheet>

            <CreateGameDialog
                open={isCreateGameDialogOpen}
                onOpenChange={setIsCreateGameDialogOpen}
            />
        </div>
    );
}
